import { useCallback, useEffect, useMemo, useState } from 'react';
import { CSSTransition, SwitchTransition } from 'react-transition-group';
import arrowLeft from '~/assets/icons/arrow_left.svg';
import car from '~/assets/icons/car.svg';
import flag from '~/assets/icons/flag.svg';
import wallet from '~/assets/icons/wallet.svg';
import { playSound, Sound } from '~/assets/sounds';
import { CollectionImage, CurrencyIcon } from '~/components/collections';
import { Button, ButtonColors, ButtonHeight } from '~/components/ui/Button';
import { Divider } from '~/components/ui/Divider';
import { SectionHeading } from '~/components/ui/SectionHeading';
import { Text } from '~/components/ui/Text';
import { useWalletIcon } from '~/hooks';
import { rage, useObservable } from '~/rage';
import {
	Currency,
	IVehicleRegistrationDetails,
	ModalButtonColor,
	ModalType,
	WalletType,
} from '~/shared';
import {
	setRegion,
	setStage,
	StageTypes,
	useLayoutDispatch,
	useLayoutSelector,
} from '../../redux';
import {
	StyledButtonPayContent,
	StyledButtonsContainer,
	StyledCard,
	StyledCardsContainer,
	StyledCircleButton,
	StyledCollectionImageWrapper,
	StyledColorContainer,
	StyledContainer,
	StyledPrice,
	StyledPriceContainer,
	StyledSection,
	StyledVehicleNameContainer,
	StyledVehicleSelectionContainer,
	StyledVINContainer,
} from './VehicleRegistrationMain.styled';

const VehicleRegistrationMain = () => {
	const dispatch = useLayoutDispatch();
	const vehicleList = useObservable(rage.vehicles.getPlayerVehicleList());
	const registrationPrice = useObservable(
		rage.vehicleRegistration.getRegistrationPrice(),
	);
	const region = useLayoutSelector(
		(state) => state.vehicleRegistrationSlice.region,
	);
	const [activeVehicle, setActiveVehicle] = useState(0);
	const [details, setDetails] = useState<IVehicleRegistrationDetails | null>(
		null,
	);
	const rubleIcon = useWalletIcon(Currency.Ruble, true);
	const modalState = useObservable(rage.modal.getCurrentModal());

	const vehicleListNonRegistered = useMemo(
		() => vehicleList.filter((e) => !e.isRegistered),
		[vehicleList],
	);

	const handlePay = useCallback(() => {
		if (!region || modalState) {
			return;
		}

		rage.modal.showModal(
			{
				type: ModalType.Price,
				title: 'Подтверждение',
				text: `Вы действительно хотите зарегистрировать транспорт ${details?.modelName} на ${region.code.padStart(
					2,
					'0',
				)} регион?`,
				acceptButton: { text: 'Оплатить', color: ModalButtonColor.Success },
				cancelButton: { text: 'Отменить', color: ModalButtonColor.Secondary },
				price: registrationPrice || 0,
				wallet: WalletType.Cash,
			},
			(result) => {
				if (result.isAccepted && region) {
					if (vehicleListNonRegistered?.[activeVehicle]?.id) {
						rage.vehicleRegistration
							.register(
								vehicleListNonRegistered?.[activeVehicle]?.id,
								region.code,
							)
							.catch((err) => rage.notifications.showException(err));
						rage.vehicleRegistration
							.exitMenu()
							.catch((err) => rage.notifications.showException(err));
						dispatch(setRegion(null));
					}
				}
			},
		);
	}, [
		region,
		registrationPrice,
		vehicleListNonRegistered,
		activeVehicle,
		details,
		region,
		modalState,
	]);

	useEffect(() => {
		if (!vehicleListNonRegistered?.[activeVehicle]?.id) {
			return;
		}

		rage.vehicleRegistration
			.getDetails(vehicleListNonRegistered?.[activeVehicle]?.id)
			.then(setDetails)
			.catch((err) => rage.notifications.showException(err));
	}, [vehicleListNonRegistered, activeVehicle]);

	useEffect(() => {
		if (!vehicleListNonRegistered.length) {
			rage.vehicleRegistration
				.exitMenu()
				.catch((err) => rage.notifications.showException(err));
		}
	}, [vehicleListNonRegistered]);

	return (
		<StyledContainer>
			<StyledSection>
				<SectionHeading text={'Выберите транспорт'} icon={car} />
				<StyledVehicleSelectionContainer>
					<StyledCircleButton
						onClick={() => {
							setActiveVehicle((prev) =>
								prev === 0 ? vehicleListNonRegistered?.length - 1 : prev - 1,
							);
							playSound(Sound.ClickButton);
						}}
						$disabled={
							!vehicleListNonRegistered.length ||
							vehicleListNonRegistered?.length === 1
						}
						onMouseEnter={() => {
							if (
								vehicleListNonRegistered.length &&
								vehicleListNonRegistered.length > 1
							) {
								playSound(Sound.LinkCursor);
							}
						}}
					>
						<img src={arrowLeft} />
					</StyledCircleButton>
					<SwitchTransition mode="out-in">
						<CSSTransition
							timeout={300}
							key={vehicleListNonRegistered?.[activeVehicle]?.id}
							classNames={'vehicle'}
							addEndListener={(node, done) =>
								node.addEventListener('transitionend', done, false)
							}
						>
							<StyledCollectionImageWrapper>
								{vehicleListNonRegistered?.[activeVehicle] ? (
									<CollectionImage
										collection={'vehicles'}
										id={vehicleListNonRegistered?.[activeVehicle]?.model}
										size={'md'}
									/>
								) : null}
							</StyledCollectionImageWrapper>
						</CSSTransition>
					</SwitchTransition>
					<StyledCircleButton
						onClick={() => {
							setActiveVehicle((prev) =>
								prev === vehicleListNonRegistered?.length - 1 ? 0 : prev + 1,
							);
							playSound(Sound.ClickButton);
						}}
						$disabled={
							!vehicleListNonRegistered.length ||
							vehicleListNonRegistered?.length === 1
						}
						onMouseEnter={() => {
							if (
								vehicleListNonRegistered.length &&
								vehicleListNonRegistered.length > 1
							) {
								playSound(Sound.LinkCursor);
							}
						}}
					>
						<img src={arrowLeft} style={{ transform: 'rotate(180deg)' }} />
					</StyledCircleButton>
				</StyledVehicleSelectionContainer>
				<StyledVehicleNameContainer>
					<Text>{details?.modelName}</Text>
					<StyledColorContainer>
						<Text>Цвет</Text>
						<div
							className={'circle'}
							style={{ backgroundColor: `#${details?.bodyColor}` }}
						/>
					</StyledColorContainer>
					<StyledVINContainer>
						<Text>
							<span>VIN:</span> {details?.vinNumber}
						</Text>
					</StyledVINContainer>
				</StyledVehicleNameContainer>
				<StyledCardsContainer>
					<StyledCard>
						<Text>Кузов</Text>
						<Text>{details?.bodyType}</Text>
					</StyledCard>
					<StyledCard>
						<Text>Гос. цена</Text>
						<Text>{details?.priceCash?.toLocaleString('ru-RU')}</Text>
					</StyledCard>
					<StyledCard>
						<Text>Пробег</Text>
						<Text>{details?.mileage?.toLocaleString('ru-RU')} км.</Text>
					</StyledCard>
				</StyledCardsContainer>
				<Divider $direction={'H'} $color={'rgba(255,255,255,0.1)'} />
			</StyledSection>
			<StyledSection>
				<SectionHeading text={'Выберите регион'} icon={flag} />
				<Button
					color={region ? ButtonColors.Select : ButtonColors.Default}
					height={ButtonHeight.Height50}
					onClick={() => dispatch(setStage(StageTypes.RegionSelection))}
				>
					{region ? (
						<Text>
							{region.code.padStart(2, '0')}{' '}
							<span style={{ opacity: 0.5 }}>({region.name})</span>
						</Text>
					) : (
						<Text>Выбрать регион гос. номера</Text>
					)}
				</Button>
				<Divider $direction={'H'} $color={'rgba(255,255,255,0.1)'} />
			</StyledSection>
			<StyledPriceContainer>
				<Text>Стоимость регистрации</Text>
				<StyledPrice>
					<Text>{registrationPrice?.toLocaleString('ru-RU')}</Text>
					<CurrencyIcon icon={rubleIcon} size="sm" />
				</StyledPrice>
			</StyledPriceContainer>
			<StyledButtonsContainer>
				<Button
					color={ButtonColors.Default}
					height={ButtonHeight.Height50}
					onClick={() =>
						rage.vehicleRegistration
							.exitMenu()
							.catch((err) => rage.notifications.showException(err))
					}
				>
					<Text>Отменить</Text>
				</Button>
				<Button
					color={
						region && vehicleListNonRegistered?.[activeVehicle]
							? ButtonColors.Buy
							: ButtonColors.Default
					}
					height={ButtonHeight.Height50}
					disabled={!region || !vehicleListNonRegistered?.[activeVehicle]}
					onClick={handlePay}
					style={{ padding: '0px 7px' }}
				>
					{!vehicleListNonRegistered?.[activeVehicle]?.id ? (
						<Text>Выберите транспорт</Text>
					) : region ? (
						<StyledButtonPayContent>
							<img src={wallet} />
							<Text>Оплатить</Text>
						</StyledButtonPayContent>
					) : (
						<Text>Выберите регион</Text>
					)}
				</Button>
			</StyledButtonsContainer>
		</StyledContainer>
	);
};

export { VehicleRegistrationMain };
